import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet,
  Copy,
  Check,
  Sparkles,
  Fingerprint,
  BadgeCheck,
  ExternalLink,
  Gift,
  Share2,
} from "lucide-react";

import { api, formatCurrency, formatDate, truncHash } from "@/lib/api";
import ShareCertificateDialog from "@/components/ShareCertificateDialog";

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  } catch {
    toast.error("Copy failed");
  }
};

const NFTCard = ({ r }) => {
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const item = r.items?.[0] || {};

  const onCopyHash = async () => {
    await copy(r.tx_hash);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <>
    <div
      data-testid={`nft-card-${r.id}`}
      className="nft-shimmer group relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-gray-50/70 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgba(0,85,255,0.12)] hover:-translate-y-1 transition-all duration-300"
    >
      <div className="grain" />

      {/* Visual — premium product treatment */}
      {r.product_image_url ? (
        <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#0f1f3a] to-[#0a1628]">
          <img
            data-testid={`nft-product-image-${r.id}`}
            src={r.product_image_url}
            alt={item.name || r.store_name}
            className="absolute inset-0 w-full h-full object-contain p-6 drop-shadow-[0_8px_24px_rgba(0,0,0,0.45)] group-hover:scale-105 transition-transform duration-700"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 text-[10px] font-semibold tracking-wider uppercase text-gray-700 px-2 py-1 rounded-md">
            <Sparkles size={11} className="text-[#0055FF]" />
            Verified · Certified
          </div>
          <div className="absolute top-3 right-3 font-mono text-[10px] bg-black/70 text-white px-2 py-1 rounded-md backdrop-blur-sm">
            {r.nft_id}
          </div>
        </div>
      ) : (
        <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
          <img
            src="https://images.unsplash.com/photo-1734792314921-e2ab82d18921?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            alt={r.store_name}
            className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white/20 via-transparent to-transparent" />
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 text-[10px] font-semibold tracking-wider uppercase text-gray-700 px-2 py-1 rounded-md">
            <Sparkles size={11} className="text-[#0055FF]" />
            Verified · Certified
          </div>
          <div className="absolute top-3 right-3 font-mono text-[10px] bg-black/70 text-white px-2 py-1 rounded-md backdrop-blur-sm">
            {r.nft_id}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="p-5">
        <div className="font-display font-bold text-gray-900 text-lg leading-tight">
          {item.name || "Item"}
        </div>
        <div
          data-testid={`nft-issued-by-${r.id}`}
          className="mt-1 text-xs text-gray-500"
        >
          Issued by{" "}
          <span className="text-gray-700 font-medium">{r.store_name}</span>
        </div>

        <div className={`mt-4 ${Number.isFinite(r.total) && r.total > 0 ? "grid grid-cols-2 gap-3" : ""} text-sm`}>
          <div>
            <div
              data-testid={`nft-date-label-${r.id}`}
              className="text-[10px] font-semibold tracking-wider uppercase text-gray-400"
            >
              {Number.isFinite(r.total) && r.total > 0 ? "Purchase" : "Issued"}
            </div>
            <div className="font-medium text-gray-900 mt-0.5">
              {formatDate(r.purchase_date)}
            </div>
          </div>
          {Number.isFinite(r.total) && r.total > 0 && (
            <div className="text-right" data-testid={`nft-price-${r.id}`}>
              <div className="text-[10px] font-semibold tracking-wider uppercase text-gray-400">
                Price
              </div>
              <div className="font-mono text-gray-900 mt-0.5">
                {formatCurrency(r.total, r.currency)}
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold tracking-wider uppercase text-gray-400">
              Tx hash
            </span>
            <button
              onClick={onCopyHash}
              data-testid={`copy-tx-${r.id}`}
              className="font-mono text-xs text-gray-700 hover:text-[#0055FF] inline-flex items-center gap-1.5 transition"
            >
              {truncHash(r.tx_hash, 8, 6)}
              {copied ? <Check size={12} /> : <Copy size={12} />}
            </button>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold tracking-wider uppercase text-gray-400">
              Custody
            </span>
            <span
              data-testid={`custody-${r.id}`}
              className="text-[11px] font-semibold text-[#0055FF] inline-flex items-center gap-1.5"
            >
              <Wallet size={11} />
              Owned via Authera Wallet
            </span>
          </div>
          {item.sku && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold tracking-wider uppercase text-gray-400">
                SKU
              </span>
              <span
                data-testid={`nft-sku-${r.id}`}
                className="font-mono text-xs text-gray-500"
              >
                {item.sku}
              </span>
            </div>
          )}
        </div>

        {(r.utilities || []).length > 0 && (
          <div
            data-testid={`benefits-indicator-${r.id}`}
            className="mt-3 flex items-center gap-2 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2"
          >
            <Gift size={13} className="text-[#0055FF] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-[#0055FF]">
                {r.utilities.length} benefit{r.utilities.length === 1 ? "" : "s"} included
              </div>
              <div className="text-[10px] text-gray-500 truncate">
                {r.utilities[0].title}
                {r.utilities.length > 1 && ` +${r.utilities.length - 1} more`}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link
            to={`/verify/${r.nft_id}`}
            data-testid={`verify-link-${r.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-900 text-xs font-semibold px-3 py-2 rounded-lg hover:border-[#0055FF] hover:text-[#0055FF] hover:-translate-y-0.5 transition-all"
          >
            <BadgeCheck size={13} />
            View
          </Link>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            disabled={!r.tx_hash}
            data-testid={`share-btn-${r.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-[#0055FF] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#0044CC] hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Share2 size={13} />
            Share
          </button>
        </div>
        {r.claim_token && r.claim_status === "pending" && (
          <Link
            to={`/claim/${r.claim_token}`}
            data-testid={`claim-link-${r.id}`}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-gray-900 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-black transition"
          >
            <Wallet size={13} />
            Claim to My Wallet
          </Link>
        )}
      </div>
      <ShareCertificateDialog
        open={shareOpen}
        txHash={r.tx_hash}
        itemName={item.name}
        onClose={() => setShareOpen(false)}
      />
    </div>
    </>
  );
};

export default function Customer() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/receipts/minted");
      setReceipts(data || []);
    } catch {
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const total = receipts.reduce((s, r) => s + (r.total || 0), 0);

  return (
    <div data-testid="customer-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
        <div>
          <div className="text-xs font-semibold tracking-wider uppercase text-[#0055FF]">
            Customer Certificates
          </div>
          <h1 className="font-display font-bold tracking-tight text-gray-900 text-3xl sm:text-4xl mt-1">
            Your Certificate Records
          </h1>
          <p className="mt-2 text-gray-600 max-w-xl">
            Portable certificates of authenticity for your most valuable
            purchases.
          </p>
        </div>

        <div
          data-testid="wallet-summary"
          className="inline-flex items-center gap-4 bg-white border border-gray-200 rounded-xl px-5 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
        >
          <div className="h-10 w-10 rounded-lg bg-blue-50 text-[#0055FF] flex items-center justify-center">
            <Wallet size={18} />
          </div>
          {total > 0 && (
            <>
              <div data-testid="wallet-total-holdings">
                <div className="text-[10px] font-semibold tracking-wider uppercase text-gray-500">
                  Total holdings
                </div>
                <div className="font-display font-bold text-xl text-gray-900">
                  {formatCurrency(total)}
                </div>
              </div>
              <div className="h-10 w-px bg-gray-200 mx-2" />
            </>
          )}
          <div>
            <div className="text-[10px] font-semibold tracking-wider uppercase text-gray-500">
              Certificates
            </div>
            <div className="font-display font-bold text-xl text-gray-900">
              {receipts.length}
            </div>
          </div>
        </div>
      </div>

      {/* Wallet address */}
      <div className="bg-gradient-to-r from-[#0055FF] to-indigo-600 rounded-2xl p-6 text-white mb-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1687463221023-02f259da7d77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold tracking-wider uppercase text-white/70 mb-2">
              Demo wallet
            </div>
            <div className="font-mono text-lg sm:text-xl font-medium">
              0x7a3b…f9e2
            </div>
            <div className="text-sm text-white/80 mt-1">
              Non-custodial · simulated network
            </div>
          </div>
          <Fingerprint size={56} strokeWidth={1.2} className="text-white/40" />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="py-24 text-center text-gray-500">Loading certificates…</div>
      ) : receipts.length === 0 ? (
        <div
          data-testid="empty-wallet"
          className="py-24 text-center bg-white rounded-xl border border-gray-200"
        >
          <div className="mx-auto h-12 w-12 rounded-xl bg-blue-50 text-[#0055FF] flex items-center justify-center">
            <Wallet size={22} />
          </div>
          <div className="mt-4 font-display font-semibold text-gray-900 text-lg">
            No certificates yet
          </div>
          <div className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
            When a retailer issues a digital certificate, it will appear here as
            a verifiable, portable record.
          </div>
        </div>
      ) : (
        <div
          data-testid="nft-grid"
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger"
        >
          {receipts.map((r) => (
            <NFTCard key={r.id} r={r} />
          ))}
        </div>
      )}
    </div>
  );
}
